import {
  projectHoursPurchaseListService,
  projectPurchaseDownload,
} from "@/service/project.service";
import { ProjectHoursPurchaseList } from "@/types/project.model";
import { errorToast, successToast } from "@/utils/toast";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import Modal from "./Modal";
import moment from "moment";

interface ProjectSummaryProps {
  clientName?: string;
  projectName?: string;
  projectId?: string; // Add projectId prop
  startDate: string;
  endDate: string;
  hoursEstimated: string;
  hoursUtilized: string;
  status: string;
  remainingHours: string;
  // Removed onViewReport as it's not being used
}

const ProjectSummary: React.FC<ProjectSummaryProps> = ({
  clientName,
  projectName,
  projectId,
  startDate,
  endDate,
  hoursEstimated,
  hoursUtilized,
  status,
  remainingHours,
}) => {
  const navigate = useNavigate();
  const [isHoursModalOpen, setIsHoursModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"estimated" | "utilized">(
    "estimated"
  );
  const [purchasedHours, setPurchasedHours] = useState<
    ProjectHoursPurchaseList[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Handle view report click
  const handleViewReport = (type: "estimated" | "utilized") => {
    // For estimated hours, show the modal with purchased hours data
    if (type === "estimated" && projectId) {
      setLoading(true);
      projectHoursPurchaseListService(projectId)
        .then((response) => {
          setPurchasedHours(response.data.data);
          setModalType(type);
          setIsHoursModalOpen(true);
        })
        .catch((err) => {
          console.error("Error fetching purchased hours:", err);
          errorToast("Error", "Failed to load monthly breakdown data");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (type === "estimated" && !projectId) {
      console.warn("Project ID is required to fetch purchased hours data");
      errorToast(
        "Warning",
        "Project ID is required to fetch purchased hours data"
      );
    } else if (type === "utilized" && projectId) {
      // For utilized hours, navigate to All Time Entries page with project filter
      navigate(`/reports/time-entries?projectId=${projectId}`);
    } else if (type === "utilized" && !projectId) {
      console.warn("Project ID is required to view time entries");
      errorToast("Warning", "Project ID is required to view time entries");
    }
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "csv" | "pdf") => {
    if (!projectId) {
      console.warn("Project ID is required to export purchased hours data");
      errorToast(
        "Warning",
        "Project ID is required to export purchased hours data"
      );
      return;
    }

    try {
      const response = await projectPurchaseDownload(type, projectId);

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type:
          type === "csv"
            ? "text/csv"
            : "application/pdf",
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      const formatType = type === "csv" ? "csv" : "pdf";

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `purchase_data_${moment().format("DD_MM_YYYY")}.${formatType}`
      );

      // Append the anchor to the body
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successToast(
        "Export Successful",
        `Time entries exported as ${type.toUpperCase()} successfully`
      );
    } catch (error) {
      console.error(`Error exporting time entries as ${type}:`, error);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setIsHoursModalOpen(false);
  };

  return (
    <div className="my-5">
      <div className="bg-[#fafafa] rounded-t-lg">
        <p className="text-[16px] font-medium px-5 py-4">Project summary</p>
      </div>
      <div
        className="px-3 sm:px-5 py-4 rounded-b-lg"
        style={{
          boxShadow: "0px 10px 60px 0px #E2ECF980",
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-3 sm:gap-x-5 gap-y-4">
          <div className="col-span-1 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-x-3 sm:gap-x-5 gap-y-4">
            {clientName && (
              <div className="flex flex-col sm:flex-row gap-1.5">
                <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                  <p className="text-[14px] font-medium text-[#1E1E1E]">
                    Client name
                  </p>
                </div>
                <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                  :
                </div>
                <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                  <p className="text-[14px] text-[#1E1E1E]">{clientName}</p>
                </div>
              </div>
            )}

            {projectName && (
              <div className="flex flex-col sm:flex-row gap-1.5">
                <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                  <p className="text-[14px] font-medium text-[#1E1E1E]">
                    Project name
                  </p>
                </div>
                <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                  :
                </div>
                <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                  <p className="text-[14px] text-[#1E1E1E]">{projectName}</p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-1.5">
              <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                <p className="text-[14px] font-medium text-[#1E1E1E]">
                  Hours estimated
                </p>
              </div>
              <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                :
              </div>
              <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                {hoursEstimated? <div className="flex flex-col lg:flex-row flex-wrap lg:gap-4 gap-1">
                  <p className="text-[14px] text-[#1E1E1E]">
                    {hoursEstimated} hours
                  </p>
                  <div
                    className="underline text-[#007FFF] cursor-pointer"
                    onClick={() => handleViewReport("estimated")}
                  >
                    View Report
                  </div>
                </div>:"-"}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-1.5">
              <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                <p className="text-[14px] font-medium text-[#1E1E1E]">
                  Start date
                </p>
              </div>
              <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                :
              </div>
              <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                <p className="text-[14px] text-[#1E1E1E]">
                  {moment(startDate, "DD-MM-YYYY").format("MM/DD/YYYY")}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-1.5">
              <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                <p className="text-[14px] font-medium text-[#1E1E1E]">
                  Hours utilized
                </p>
              </div>
              <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                :
              </div>
              <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                <div className="flex flex-col lg:flex-row flex-wrap lg:gap-4 gap-1">
                  <p className="text-[14px] text-[#1E1E1E]">
                    {hoursUtilized} hours
                  </p>
                  <div
                    className="underline text-[#007FFF] cursor-pointer"
                    onClick={() => handleViewReport("utilized")}
                  >
                    View Report
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-1.5">
              <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                <p className="text-[14px] font-medium text-[#1E1E1E]">
                  End date
                </p>
              </div>
              <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                :
              </div>
              <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                <p className="text-[14px] text-[#1E1E1E]">
                  {endDate ? moment(endDate, "DD-MM-YYYY").format("MM/DD/YYYY") : "-"}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-1.5">
              <div className="w-full sm:w-[172px] justify-center items-center py-2 sm:py-3">
                <p className="text-[14px] font-medium text-[#1E1E1E]">Status</p>
              </div>
              <div className="hidden sm:block w-[24px] justify-center items-center py-3 font-medium">
                :
              </div>
              <div className="w-full sm:min-w-[236px] h-auto sm:h-[42px] justify-center items-center py-2 sm:py-3 px-2 sm:px-3">
                <div
                  className={`w-[90px] h-[32px] py-0.5 px-1 items-center justify-center flex
                  ${
                    status === "Active" || status === "Completed"
                      ? "bg-[#EDFFF9] text-[#0C7453]"
                      : "bg-[#EDFFF9] text-[#FF3B30]"
                  }
                  `}
                >
                  {status}
                </div>
              </div>
            </div>
          </div>
          {remainingHours !== "0" && <div className="col-span-1 lg:col-span-1 mt-4 lg:mt-0">
            <div className="flex flex-col w-full sm:w-[236px] h-[112px] bg-[#fafafa] justify-center items-center rounded-lg mx-auto">
              <p className="text-2xl font-medium text-[#1E1E1E] mb-[7px]">
                Remaining hours
              </p>
              <p
                className={`text-2xl font-bold ${
                  parseFloat(remainingHours) < 0
                    ? "text-[#FF3B30]"
                    : "text-[#00A86B]"
                }`}
              >
                {remainingHours} hours
              </p>
            </div>
          </div>}
        </div>
      </div>

      {/* Hours Report Modal */}
      <Modal
        isOpen={isHoursModalOpen}
        onClose={handleCloseModal}
        title={modalType === "estimated" ? "Hours estimated" : "Hours utilized"}
        width="min(95%, 868px)"
        contentClassName="p-0 max-w-[868px] w-full mx-auto"
      >
        <div className="mb-5 p-2 sm:p-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {loading ? (
            <div className="py-4 text-center">
              Loading purchased hours data...
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="min-w-full divide-y divide-[#E0E0E0] border border-t-0 border-r-0 border-[#E0E0E0] table-fixed sm:table-auto">
                <thead className="bg-[#F4F7F9]">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[14px] font-[600] text-[#1E1E1E] w-1/2">
                      Purchased on
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[14px] font-[600] text-[#1E1E1E] w-1/2">
                      Hours purchased
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E0E0E0]">
                  {purchasedHours.length > 0 ? (
                    purchasedHours.map((item, index) => (
                      <tr key={item.id || index}>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium w-1/2">
                          {moment(item.created_on, "DD-MM-YYYY").format(
                            "MM/DD/YYYY"
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium w-1/2">
                          {item.esti_hrs}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-2 sm:px-4 py-2 sm:py-3 text-center text-[14px] text-[#969696]"
                      >
                        No purchased hours data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-center sm:justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={() => handleExport("pdf")}
            className="px-4 py-2 text-[14px] w-full sm:w-[140px] text-gray-700 bg-[#F5F5F5] font-[500] rounded-md hover:bg-gray-50 cursor-pointer"
          >
            Export as PDF
          </button>
          <button
            type="button"
            onClick={() => handleExport("csv")}
            className="w-full sm:w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed"
          >
            Export as CSV
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectSummary;
